import React, { useEffect, useMemo, useState } from 'react';
import { Page, Document, pdfjs } from 'react-pdf';
import { useWindowWidth } from '@react-hook/window-size';

import { Spinner } from '@fw-nextgen/fw-components';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfViewer.scss';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface IProps {
  fileDownloadUrl: string;
  onPdfFileLoaded?: () => void;
}

const CONTAINER_WIDTH_XL = 0.8;
const CONTAINER_WIDTH_LG = 0.9;
const LG = 1024;

const INITIAL_PAGES_NUMBER = 1;
const INITIAL_LOADED_PAGES_AMOUNT = 0;

const PdfViewer = ({ fileDownloadUrl, onPdfFileLoaded }: IProps) => {
  const windowWidth = useWindowWidth();
  const [loadedPagesAmount, setLoadedPagesAmount] = useState(0);
  const [pagesNumber, setPagesNumber] = useState(INITIAL_PAGES_NUMBER);

  const containerWidth = useMemo(() => {
    if (windowWidth < LG) {
      return windowWidth * CONTAINER_WIDTH_LG;
    }

    return windowWidth * CONTAINER_WIDTH_XL - 5;
  }, [windowWidth]);

  const onDocumentLoaded = ({ numPages }: { numPages: number }) => {
    setPagesNumber(numPages);
  };

  const onPageLoaded = () => {
    setLoadedPagesAmount(prevState => prevState + 1);
  };

  useEffect(() => {
    if (loadedPagesAmount === pagesNumber) {
      onPdfFileLoaded?.();
    }
  }, [loadedPagesAmount, pagesNumber]);

  useEffect(() => {
    setPagesNumber(INITIAL_PAGES_NUMBER);
    setLoadedPagesAmount(INITIAL_LOADED_PAGES_AMOUNT);
  }, [fileDownloadUrl]);

  return (
    <Document
      file={fileDownloadUrl}
      className="pdf-viewer"
      loading={<Spinner />}
      onLoadSuccess={onDocumentLoaded}
      noData={''}
    >
      {Array.from(new Array(pagesNumber), (el, index) => (
        <Page
          key={`page_${index + 1}`}
          className="pdf-viewer__page"
          pageNumber={index + 1}
          width={containerWidth}
          onLoadSuccess={onPageLoaded}
          loading=""
        />
      ))}
    </Document>
  );
};

export default PdfViewer;
